// @ts-nocheck
import { formGlobals } from './_/form-globals';
import BaseInput from './_/base-input';

/**
 * Class: RadioGroupInput
 * Purpose: Manage a <div role="radiogroup"> with N <input type="radio">, handle validity (required),
 *          propagate ARIA (aria-describedby on each radio), and emit events like other inputs.
 *
 * Constructor Parameters (types):
 *   wrap (HTMLElement)      - The .form_field-wrap containing the radiogroup, note, and error.
 *   formIndex (number|null) - Index of the parent form instance.
 *   wrapIndex (number|null) - Index of this field within the form instance.
 *
 * Public Methods:
 *   isValid(): boolean
 *     // Returns group validity (true if a radio is selected when required, otherwise true).
 *
 *   getState():
 *     // Returns current snapshot:
 *     // { value:string|null, label:string|null, selectedIndex:number, valid:boolean,
 *     //   blurred:boolean, required:boolean, id:string }
 *
 *   validateNow(): void
 *     // Forces validation now.
 *
 *   destroy(): void
 *     // Removes listeners and references.
 *
 * Events:
 *   'validchange' (detail:{ valid:boolean, value:string|null, label:string|null, instance:RadioGroupInput })
 *   'valuechange' (detail:{ value:string|null, label:string|null, selectedIndex:number, instance:RadioGroupInput })
 *   'blur'        (detail:{ instance:RadioGroupInput })
 *   'focus'       (detail:{ instance:RadioGroupInput })
 * 
 * @class
 * @extends {BaseInput}
 * @this {RadioGroupInput}
 */
export default class RadioGroupInput extends BaseInput {
  /**
   * @param {HTMLElement} wrap
   * @param {number|null} formIndex
   * @param {number|null} wrapIndex
   */
  constructor(wrap, formIndex = null, wrapIndex = null) {
    super(wrap, formIndex, wrapIndex);

    // Struktur
    /** @type {HTMLElement|null} */
    this.group = wrap.querySelector('[role="radiogroup"]');
    /** @type {HTMLInputElement[]} */
    this.radios = Array.from(wrap.querySelectorAll('input[type="radio"]'));
    this.list = wrap.querySelector('.form_field-group_radio_list') || this.group;

    // IDs
    this.inputId = `radiogroup-${formIndex}-${wrapIndex}`;
    this.errorId = `${this.inputId}-error`;

    // Initialize base class elements
    this._initErrorElement();
    this._initNoteElement(this.group?.querySelector(formGlobals.noteSelector) || null);

    // Required-Quelle: radiogroup ODER irgendein Radio
    const groupReq = this.group?.getAttribute('aria-required') === 'true';
    const itemReq = this.radios.some((r) => r.getAttribute('aria-required') === 'true');
    this.required = groupReq || itemReq;

    // State
    const selInfo = this._currentSelection();
    this.value = selInfo.value;
    this.label = selInfo.label;
    this.selectedIndex = selInfo.index; // -1 wenn keiner gewählt
    this.valid = this.required ? this.selectedIndex !== -1 : true;
    this.blurred = false;

    // Listener-Verwaltung
    this._onFocusIn = this._onFocusIn.bind(this);
    this._onFocusOut = this._onFocusOut.bind(this);
    this._onChange = this._onChange.bind(this);

    this._setAttributes();
    this._normalizeRequiredARIA(); // nur Gruppe trägt aria-required
    this._applyNameFromDataAttribute(); // data-name vom group auf alle Radios anwenden
    this._initEvents();
    this._setFilledClass();
  }

  /** @returns {HTMLInputElement[]} */
  _getAriaElement() {
    return this.radios;
  }

  _getValidChangeDetail(): { valid: boolean; value: string | null; label: string | null; instance: RadioGroupInput } {
    return {
      valid: this.valid,
      value: this.value,
      label: this.label,
      instance: this
    };
  }

  /** @returns {boolean} */
  isValid() {
    return this.valid;
  }

  /** @returns {{value:(string|null),label:(string|null),selectedIndex:number,valid:boolean,blurred:boolean,required:boolean,id:string}} */
  getState() {
    return {
      value: this.value,
      label: this.label,
      selectedIndex: this.selectedIndex,
      valid: this.valid,
      blurred: this.blurred,
      required: this.required,
      id: this.inputId,
    };
  }

  /** Force validation now. */
  validateNow() {
    this._validate();
  }

  /** Cleanup listeners. */
  destroy() {
    super.destroy();
    this.group = null;
    this.radios = [];
  }

  // ---------- Private ----------
  /**
   * Function: _setAttributes
   * Purpose: IDs setzen, aria-describedby auf jedes Radio (Note initial), Error-/Note-IDs vergeben.
   */
  _setAttributes() {
    if (!this.group) return;

    // Gruppe: für Debug/Referenz
    if (!this.group.id) this.group.id = this.inputId;

    // Jedes Radio: initial describedby = Note (falls vorhanden)
    this._setAriaDescribedBy({ includeNote: true, includeError: false });
  }

  /**
   * Function: _normalizeRequiredARIA
   * Purpose: Best Practice – nur die Gruppe trägt aria-required. Entfernt aria-required von Items.
   */
  _normalizeRequiredARIA() {
    if (!this.group) return;
    if (this.required) {
      this.group.setAttribute('aria-required', 'true');
    } else {
      this.group.removeAttribute('aria-required');
    }
    this.radios.forEach((r) => r.removeAttribute('aria-required'));
  }

  /**
   * Function: _applyNameFromDataAttribute
   * Purpose: Lese data-name vom radiogroup Element und wende es als name Attribut auf alle Radios an.
   * Dies vereinfacht die Verwaltung, da nur ein Ort für die Namensgebung benötigt wird.
   */
  _applyNameFromDataAttribute() {
    if (!this.group) return;
    
    const groupName = this.group.getAttribute('data-name');
    if (groupName) {
      this.radios.forEach((radio) => {
        radio.setAttribute('name', groupName);
      });
    }
  }

  /**
   * Function: _initEvents
   * Purpose: change + focusin/out auf der Gruppe.
   */
  _initEvents() {
    if (!this.group) return;

    const o = { signal: this._ac.signal };

    // Fokusverwaltung am Container
    this.group.addEventListener('focusin', this._onFocusIn, o);
    this.group.addEventListener('focusout', this._onFocusOut, o);

    // Änderungen pro Radio
    this.radios.forEach((r) => {
      r.addEventListener('change', this._onChange, o);
    });
  }

  /**
   * Function: _onFocusIn
   * Purpose: Fokusklasse setzen & einmaliges 'focus'-Event feuern.
   */
  _onFocusIn() {
    this.wrap.classList.add(formGlobals.focusedClass);
    if (!this._focused) {
      this._focused = true;
      this._dispatch('focus', { instance: this });
    }
  }

  /**
   * Function: _onFocusOut
   * Purpose: Beim Verlassen der Gruppe 'blur' + ggf. Validierung.
   * @param {FocusEvent} e
   */
  _onFocusOut(e) {
    if (!this.group.contains(e.relatedTarget)) {
      this.wrap.classList.remove(formGlobals.focusedClass);
      const wasFirst = !this.blurred;
      this.blurred = true;
      this._focused = false;
      this._dispatch('blur', { instance: this });
      if (wasFirst && this.required) this._validate();
    }
  }

  /**
   * Function: _onChange
   * Purpose: Auswahl aktualisieren, 'valuechange' emittieren und (falls nötig) validieren.
   */
  _onChange() {
    const prevVal = this.value;
    const sel = this._currentSelection();
    this.value = sel.value;
    this.label = sel.label;
    this.selectedIndex = sel.index;

    this._setFilledClass();

    if (prevVal !== this.value) {
      this._dispatch('valuechange', {
        value: this.value,
        label: this.label,
        selectedIndex: this.selectedIndex,
        instance: this,
      });
    }

    if (this.blurred || this.required) {
      this._validate();
    }
  }

  /**
   * Function: _currentSelection
   * Purpose: Liefert derzeit ausgewählte Radio-Option.
   * @returns {{ value:(string|null), label:(string|null), index:number }}
   */
  _currentSelection() {
    const idx = this.radios.findIndex((r) => r.checked);
    if (idx === -1) return { value: null, label: null, index: -1 };
    const radio = this.radios[idx];
    const labelEl = radio.closest('label')?.querySelector('.form_field-radio_label');
    const label = (labelEl?.textContent || '').trim() || radio.value || null;
    const value = radio.value ?? label;
    return { value, label, index: idx };
  }

  /**
   * Function: _setFilledClass
   * Purpose: Toggle filled-Klasse je nach Auswahl.
   */
  _setFilledClass() {
    if (this.selectedIndex !== -1) {
      this.wrap.classList.add(formGlobals.filledClass);
    } else {
      this.wrap.classList.remove(formGlobals.filledClass);
    }
  }

  /**
   * Function: _validate
   * Purpose: Gruppenvalidierung (bei required: genau eine Option gewählt).
   */
  _validate() {
    const ok = this.required ? this.selectedIndex !== -1 : true;
    if (ok) this._setValid();
    else this._setInvalid();
  }
}
